<template>
    <m-pageframe
        title="Bulk Message Details"
        :color="pageframe_color"
    >
    <v-container
        v-if="bulk_message != null"
        style="padding-left: 25px; padding-right: 25px;"
        fluid
    >
        <provision-warning-bulk-messages />

        <v-alert
            border="left"
            v-if="is_scheduled_but_unsent"
            color="primary"
            colored-border
            type="info"
            elevation="2"
        >
            This bulk message is schedueld to be sent on {{$rc.pretty(bulk_message.scheduled_send_time,'date')}} at (or around) {{$rc.pretty(bulk_message.scheduled_send_time,'time')}}.
            <br/><br/>
            <a v-on:click="onClickCancelScheduledSend">Click here to cancel the scheduled send</a>
        </v-alert>

        <v-alert
            border="left"
            v-if="!has_sent"
            color="blue"
            colored-border
            elevation="2"
        >
            <div
                style="display: flex"
            >
                <div
                    style="flex-grow: 1"
                >  
                    <v-chip
                        dense
                        color="blue"
                        style="color: white;"
                        label
                    >
                        New
                    </v-chip>

                    Optimize your message's recipients using RedChirp’s AI recipient list optimization!
                </div>
                <div
                    style="flex-grow: 0"
                >
                    <v-btn
                        v-on:click="onClickNewAIDetails"
                    >
                        Details...
                    </v-btn>
                </div>
            </div>
            
        </v-alert>

        <v-alert
            border="left"
            v-if="has_sent"
            color="primary"
            colored-border
            type="info"
            elevation="2"
        >
            This message {{ is_scheduled_but_unsent ? 'is scheduled to be sent.' : 'has been sent.' }} <br/>
            You can monitor the send process <a @click="onViewSendSummary">here.</a>
        </v-alert>

        <v-alert
            border="left"
            v-if="has_cancellation_reason"
            color="primary"
            colored-border
            type="error"
            elevation="2"
        >
            This message was cancelled. <br/>
            Reason: {{ this?.bulk_message?.cancellation_reason }}
        </v-alert>

        <m-help-banner
            label="Need Help Creating or Sending a Bulk Message?  Click Here for Help!"
            url="https://redchirp.com/?p=7167"
        />

        <v-form>

                <m-incident-banner
                    ref="incident-banner"
                    page="bulk_message"
                />
                <div style="direction: ltr;">
                    <div style="display: flex; align-items: center;">
                            <h2>Settings</h2>

                            <v-menu
                                style="margin-left: 8px;"
                                v-model="advanced_settings_menu"
                                :close-on-content-click="false"
                                offset-y
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn
                                        icon
                                        v-bind="attrs"
                                        v-on="on"
                                    >
                                        <v-icon>mdi-dots-vertical</v-icon>
                                    </v-btn>
                                </template>
                                <v-card style="padding: 16px; min-width: 300px;">
                                    <v-card-title style="padding: 0 0 16px 0;">
                                        Advanced Settings
                                    </v-card-title>
                                    <v-checkbox
                                        v-model="bulk_message.is_double_opt_in_message"
                                        label="Bulk message should start a double opt-in process"
                                        @change="onToggleDoubleOptIn"
                                        :disabled="has_sent"
                                    />
                                </v-card>
                            </v-menu>
                        </div>
                    </div>
                    <m-edit-text
                        label="Bulk Message Name"
                        :value="bulk_message.label"
                        v-on:input="updateBulkMessageLabel"
                    />

                    <m-edit-tags
                        label="Tags"
                        applies_to="bulk-message"
                        v-model="bulk_message.rc_tag_ids"
                        style="margin-top: -15px"
                        v-on:input="upsertBulkMessage"
                    />

                    <m-edit-select-organization-owned-endpoint
                        :organization_id="bulk_message.organization_id"
                        :value="bulk_message.from_endpoint"
                        v-on:input="selectTeamEndpoint"
                        :disabled="has_sent"
                    />

                    <v-select
                        label="Message Type"
                        :items="message_type_choices"
                        v-model="bulk_message.category"
                        v-on:change="updateBulkMessageType"
                        :disabled="has_sent"
                    />



                    <v-alert
                        v-if="bulk_message.is_double_opt_in_message"
                        type="warning"
                        outlined
                        class="mb-4"
                    >
                        <strong>Disclaimer</strong><br/>
                        <p>This is not legal advice. Consult an Attorney if you have legal or compliance questions about your text marketing campaigns.</p>
                        <p>RedChirp provides users with a template message that might be used to obtain affirmative written consent for marketing and promotional messages from individuals on your contact list. As with any marketing campaign, you should consider the target audience, risks of reassigned or incorrect numbers, recent interactions with the customer, and what, if any, existing consent you already have from your customer base. Keep in mind that as time passes since the customer provided you their number there's increased risk that the number no longer belongs to the person that provided it.</p>
                        <p>We recommend against adding any promotional, incentive, or marketing language to this message. When in doubt, complicated legal questions should be raised with an attorney experienced in telemarketing compliance.</p>
                    </v-alert>

                    <m-edit-textarea
                        v-if="use_old_message_patterns"
                        label="Message Text"
                        :value="old_message_text"
                        :optional="false"
                        :hint="text_message_hint_text"
                        v-on:input="updateOldBulkMessageText"
                        :use_compose_message="true"
                        :rules="[rules.no_pay_links]"
                        :read_only="has_sent"
                        :show_message_type="true"
                        :force_mms="message_type_force_mms"
                        :insert_chips="message_variables"
                    />

                    <m-edit-message-patterns-compact
                        label="Message"
                        v-model="bulk_message.message_patterns"
                        v-on:input="upsertBulkMessage()"
                        v-on:example-message-changed="onExampleMessageChanged"
                        context="bulk-message"
                        style="padding-bottom: 7px; margin-bottom: 15px; border-bottom: 1px solid rgb(148,148,148);"
                        :bulk_message_id="bulk_message.bulk_message_id"
                        v-if="!use_old_message_patterns"
                        :disabled="has_sent"
                        :always_mms="message_type_force_mms || bulk_message.vcard_id != null"
                    />

                    <v-select
                        v-if="show_shorten_links_using"
                        label="Shorten Links Using"
                        :items="link_shortener_host_choices"
                        v-model="bulk_message.link_shortener_host"
                        v-on:change="upsertBulkMessage"
                        :readonly="has_sent"
                        :disabled="has_sent"
                    />

                    <div
                        v-if="!has_sent"
                        style="margin-bottom: 15px; text-align: right"
                    >
                        <v-menu
                            bottom
                            offset-y
                        >

                        <template v-slot:activator="{ on }">

                            <v-btn
                                class="mx-2" fab dark small color="primary"
                                :disabled="has_sent"
                                v-on="on"
                            >
                                <v-icon dark>mdi-dots-vertical</v-icon>
                            </v-btn>

                        </template>

                        <v-list>
                            <v-list-item
                                v-on:click="onClickChangeVariableStyle()"
                            >
                                <v-list-item-title>{{use_old_message_patterns ? 'Use new style variables (e.g. {first-name})' : 'Use legacy variables (e.g. %first_name%)'}}</v-list-item-title>
                            </v-list-item>

                            <v-divider/>

                            <v-list-item
                                v-on:click="onClickTemplates()"
                            >
                                <v-list-item-title>Templates...</v-list-item-title>
                            </v-list-item>

                             <v-divider
                                v-if="feature_available_rc_ai_improve_content"
                             />

                             <m-restricted-menu-list-item
                                v-on:click="onClickRedChirpAIImproveContent()"
                                v-if="feature_available_rc_ai_improve_content"
                                text="RedChirp AI: Suggestions to improve message content..."
                            >
                                <m-menu-chip/>
                            </m-restricted-menu-list-item>



                        </v-list>
                        </v-menu>

                        <m-model-r1-suggestions-to-improve-message-content-dialog
                            ref="m-model-r1-suggestions-to-improve-message-content-dialog"
                            v-on:update="update"
                        />
                    </div>


                    <m-edit-blobs
                        label="Image (optional)"
                        empty_text="No image"
                        v-model="bulk_message.blob_ids"
                        :process_media="true"
                        :externally_visible="true"
                        :allow_multiple="false"
                        :limit_to_size="460800"
                        v-on:input="onChangeBlobs"
                        :read_only="has_sent"
                        style="padding-bottom: 7px; margin-bottom: 15px; border-bottom: 1px solid rgb(148,148,148); margin-top: 15px"
                        edit_icon="mdi-lead-pencil"
                        dialog_title="Bulk Message Image"
                        dialog_hint="<b>Image limitations:</b>  Images <b>must</b> be 450 Kb or smaller.<br/><br/><b>Image size:  </b>For best results, images should be square or close to square.  The ideal image size is 400 x 400 pixels."
                    />

                    <m-select-vcard
                        label="Attach contact card (optional)"
                        v-model="bulk_message.vcard_id"
                        v-on:input="upsertBulkMessage"
                        ref="select-contact-card"
                        show_bulk_message_warning
                        style="margin-top: 15px"
                        :read_only="has_sent"
                    />

                    <v-chip
                        color="primary"
                        class="ma-2"
                        label
                        small
                    >
                        {{message_type}}
                    </v-chip>




                <v-select
                    label="Outcome Tracking"
                    :items="outcome_tracking_items"
                    v-model="use_outcome_tracking"
                    v-on:change="updateOutcomeTracking"
                    style="margin-top: 15px"
                />

                <div
                     v-if="use_outcome_tracking == 'yes'"
                >
                    <m-edit-text
                        v-for="(item, index) in outcomes"
                        :key="index"
                        :label="'Outcome #'+(index+1)+' Name'"
                        :value="item.label"
                        :menu_items="outcome_menu_items"
                        v-on:input="afterEditOutcome($event, item)"
                        v-on:delete="afterDeleteOutcome($event, item)"
                        :read_only="has_sent"
                    />

                    <m-icon-bar
                        v-on:add="onClickCreateOutcome"
                        v-show="!has_sent"
                    />

                </div>

                 <h2>Recipients</h2>



                <v-text-field
                    autocomplete="off"
                    label="Search"
                    prepend-icon="mdi-magnify"
                    v-model="full_text"
                    v-on:input="debounceUpdateRecipients"
                >
                    <v-menu
                        bottom
                        offset-y
                        slot="append-outer"
                    >
                        <template v-slot:activator="{ on }">
                            <v-icon
                                v-on="on"
                            >
                                mdi-dots-vertical
                            </v-icon>
                        </template>

                        <v-list>
                           <v-list>
                                <v-list-item
                                    v-on:click="update()"
                                >
                                    <v-list-item-title>Refresh</v-list-item-title>
                                </v-list-item>

                                <v-divider/>

                                <v-list-item
                                    v-on:click="onClickValidation()"
                                >
                                    <v-list-item-title>Validation...</v-list-item-title>
                                </v-list-item>

                                <v-list-item
                                    v-on:click="onClickUpdateRecipientLocations()"
                                >
                                    <v-list-item-title>Update recipient locations...</v-list-item-title>
                                </v-list-item>

                                <m-restricted-menu-list-item
                                    v-if="$rc.$state.isAuthActorSystemAdmin()"
                                    v-on:click="onClickUpdateNumberReassignmentCheckSinceDates()"
                                    text="Update number reassignment check since dates..."
                                >
                                </m-restricted-menu-list-item>

                                <v-divider/>


                                <v-list-item
                                    v-on:click="exportToExcel()"
                                >
                                    <v-list-item-title>Export to Excel...</v-list-item-title>
                                </v-list-item>

                                <v-list-item
                                    v-on:click="onClickViewImports()"
                                >
                                    <v-list-item-title>View excel files uploaded / imported...</v-list-item-title>
                                </v-list-item>

                                <v-divider
                                    v-if="!has_sent"
                                />

                                <v-list-item
                                    v-on:click="onClickCreateRecipient()"
                                    v-if="!has_sent"
                                >
                                    <v-list-item-title>Add recipient manually...</v-list-item-title>
                                </v-list-item>

                                <v-list-item
                                    v-on:click="onClickAddContacts()"
                                    v-if="!has_sent"
                                >
                                    <v-list-item-title>Add recipients from contacts...</v-list-item-title>
                                </v-list-item>

                                 <v-list-item
                                    v-on:click="onClickExcelImport()"
                                    v-if="!has_sent"
                                >
                                    <v-list-item-title>Add recipients from an Excel file...</v-list-item-title>
                                </v-list-item>

                                <v-list-item
                                    v-on:click="onClickAddAllListMembers()"
                                    v-if="!has_sent"
                                >
                                    <v-list-item-title>Add recipients from a list...</v-list-item-title>
                                </v-list-item>

                                 <m-restricted-menu-list-item
                                    v-on:click="onClickAddDummyRecipients()"
                                    v-if="this.$rc.$state.isAuthActorSystemAdmin() && bulk_message.is_demo_organization"
                                    text="Add dummy recipients..."
                                />

                                 <m-restricted-menu-list-item
                                    v-on:click="onClickAddDummySpamRecipients()"
                                    v-if="this.$rc.$state.isAuthActorSystemAdmin() && bulk_message.is_demo_organization"
                                    text="Add dummy (spam) recipients..."
                                />

                                <m-restricted-menu-list-item
                                    v-on:click="onClickDummyExport()"
                                    v-if="this.$rc.$state.isAuthActorSystemAdmin() && bulk_message.is_demo_organization"
                                    text="Export dummy list of recipients..."
                                />

                                <m-restricted-menu-list-item
                                    v-on:click="onClickWinePulseDummy()"
                                    v-if="this.$rc.$state.isAuthActorSystemAdmin() && bulk_message.is_demo_organization"
                                    text="Export dummy WinePulse report..."
                                />

                                <v-divider
                                    v-if="!has_sent"
                                />

                                <v-list-item
                                    v-on:click="onEditErrorRateBeforeCancel()"
                                    v-if="!has_sent"
                                >
                                    <v-list-item-title>Adjust max error rate...</v-list-item-title>
                                </v-list-item>

                                <v-list-item
                                    v-on:click="deleteAllRecipients()"
                                    v-if="!has_sent"
                                >
                                    <v-list-item-title>Delete all recipients...</v-list-item-title>
                                </v-list-item>

                            </v-list>
                        </v-list>
                    </v-menu>
                </v-text-field>

                <v-data-table
                    :headers="recipient_headers"
                    :items="recipients"
                    :items-per-page="50"
                    loading-text="Loading... Please wait"
                    class="elevation-1"
                    :custom-sort="customRecipientSort"
                    :loading="loading_recipients"

                    :footer-props="{
                        showFirstLastPage: true,
                        itemsPerPage: 50,
                        disableItemsPerPage: true,
                        itemsPerPageOptions: [50, 100],
                    }"

                    :search="full_text"
                    :options.sync="table_options"
                    :server-items-length="result_count"
                >
                    <template v-slot:item.recipient_contact_first_name="{ item }">
                        {{prettyFirstName(item)}}
                    </template>

                    <template v-slot:item.recipient_contact_last_name="{ item }">
                        {{prettyLastName(item)}}
                    </template>

                    <template v-slot:item.recipient_endpoint="{ item }">
                        {{$rc.pretty(item.recipient_endpoint,'endpoint_no_parens')}}
                    </template>

                    <template v-slot:item.locations="{ item }">
                        {{$rc.pretty(item.locations,'comma_list')}}
                    </template>

                    <template v-if="$rc.$state.isAuthActorSystemAdmin()" v-slot:header.number_reassignment_check_since_date>
                        <m-restricted-table-header-cell text="Number Reassignment Check Since Date" />
                    </template>
                    <template v-slot:item.number_reassignment_check_since_date="{ item }">
                        {{$rc.pretty(item.number_reassignment_check_since_date,'date')}}
                    </template>


                    <template v-slot:item.custom_message="{ item }">
                        {{prettyCustomMessage(item.custom_message)}}
                    </template>

                    <template v-slot:item.sent_time="{ item }">
                        {{prettySentTime(item)}}
                    </template>

                    <template v-slot:item.status="{ item }">
                        {{prettyStoredMessageState(item)}}
                    </template>

                    <template v-slot:item.replied="{ item }">
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <span
                                    v-bind="attrs"
                                    v-on="on"
                                >{{prettyReplied(item)}}</span>
                            </template>
                            <span>
                                This is an automated approximation of who has replied.
                                ”Yes” means the recipient has texted or called the number that sent this bulk message within 7 days of receiving it.
                            </span>
                        </v-tooltip>
                    </template>

                    <template v-slot:item.action="{ item }">
                        <v-menu
                            bottom
                            offset-y
                        >

                            <template v-slot:activator="{ on, attrs }">
                                <v-icon
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    mdi-dots-vertical-circle-outline
                                </v-icon>
                            </template>

                            <v-list>
                                <v-list-item
                                    v-if="showViewConversation(item)"
                                    v-on:click="onViewConversation(item)"
                                >
                                    <v-list-item-title>View Conversation...</v-list-item-title>
                                </v-list-item>

                                <v-list-item
                                    v-if="showViewMessageDetails(item)"
                                    v-on:click="onMessageDetails(item)"
                                >
                                    <v-list-item-title>Message Details</v-list-item-title>
                                </v-list-item>

                                <v-list-item
                                    v-if="showRetryMessage(item)"
                                    v-on:click="onRetryMessage(item)"
                                >
                                    <v-list-item-title>Retry Message</v-list-item-title>
                                </v-list-item>

                                <v-list-item
                                    v-on:click="changeOutcome(item)"
                                >
                                    <v-list-item-title>Change Outcome</v-list-item-title>
                                </v-list-item>

                                <v-list-item
                                    v-on:click="previewMessage(item)"
                                    v-if="!has_sent"
                                >
                                    <v-list-item-title>Preview Message</v-list-item-title>
                                </v-list-item>

                                <v-list-item
                                    v-on:click="onClickMessageGenerationTechnicalDetails(item)"
                                    v-if="item.message_generation_technical_details != null"
                                >
                                    <v-list-item-title>Message generation technical details...</v-list-item-title>
                                </v-list-item>

                                <v-list-item
                                    v-on:click="selectTestMessageRecipient(item)"
                                >
                                    <v-list-item-title>Send Test</v-list-item-title>
                                </v-list-item>

                                <v-list-item
                                    v-on:click="editCustomMessage(item)"
                                >
                                    <v-list-item-title>Custom Message</v-list-item-title>
                                </v-list-item>

                                <v-list-item
                                    v-on:click="onClickEditRecipient(item)"
                                    :disabled="has_sent"
                                >
                                    <v-list-item-title>Edit Recipient</v-list-item-title>
                                </v-list-item>

                                <v-list-item
                                    v-on:click="onClickLocationDetails(item)"
                                >
                                    <v-list-item-title>Location technical details...</v-list-item-title>
                                </v-list-item>

                                <m-restricted-menu-list-item
                                    v-if="$rc.$state.isAuthActorSystemAdmin()"
                                    v-on:click="onClickNumberReassignmentCheckSinceDateDetails(item)"
                                    text="Number reassignment check since date technical details..."
                                >
                                </m-restricted-menu-list-item>

                                <v-divider/>

                                <v-list-item
                                    v-on:click="deleteRecipient(item)"
                                    :disabled="has_sent"
                                >
                                    <v-list-item-title>Delete Recipient</v-list-item-title>
                                </v-list-item>

                            </v-list>
                        </v-menu>
                    </template>
                </v-data-table>

                <p style="font-size: 12px; margin-top: 7px;">* -- State and Timezone are computed on a "best effort" basis based on POS / CRM profiles, area code and other data points.  Location may be missing or inaccurate.</p>

                <div
                    style="text-align: right; margin-top: 15px;"
                    v-show="!has_sent"
                >

                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn
                                class="mx-2" fab dark small color="primary"
                                v-on:click="onClickCreateRecipient"
                                :disabled="has_sent"
                                v-bind="attrs"
                                v-on="on"
                            >
                                <v-icon dark>mdi-plus</v-icon>
                            </v-btn>
                        </template>
                        Add one recipient
                    </v-tooltip>

                     <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn
                                class="mx-2" fab dark small color="primary"
                                v-on:click="onClickAddContacts"
                                :disabled="has_sent"
                                v-bind="attrs"
                                v-on="on"
                            >
                                <v-icon dark>mdi-account-multiple-plus</v-icon>
                            </v-btn>
                        </template>
                        Add multiple recipients (using contacts)
                     </v-tooltip>

                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn
                                class="mx-2" fab dark small color="primary"
                                v-on:click="onClickExcelImport"
                                :disabled="has_sent"
                                v-bind="attrs"
                                v-on="on"
                            >
                                <v-icon dark>mdi-microsoft-excel</v-icon>
                            </v-btn>
                        </template>
                        Add recipients from an Excel file
                    </v-tooltip>

                    <!-- The add recipients from a list is not a menu -->
                    <v-tooltip
                        bottom
                        v-if="!show_add_recipients_from_list_menu"
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn
                                class="mx-2" fab dark small color="primary"
                                v-on:click="onClickAddAllListMembers"
                                :disabled="has_sent"
                                v-bind="attrs"
                                v-on="on"
                            >
                                <v-icon dark>mdi-format-list-bulleted-square</v-icon>
                            </v-btn>
                        </template>
                        Add recipients from a list
                    </v-tooltip>

                    <!-- The add recipients from a list is not a menu -->
                    <v-menu
                        bottom
                        offset-y
                        v-if="show_add_recipients_from_list_menu"
                    >

                    <template v-slot:activator="{ on }">

                        <v-btn
                            class="mx-2" fab dark small color="primary"

                            :disabled="has_sent"
                            v-on="on"
                        >
                            <v-icon dark>mdi-format-list-bulleted-square</v-icon>
                        </v-btn>

                    </template>

                        <v-list>
                            <v-list-item
                                v-for="(item, index) in add_recipients_from_list_menu_items"
                                :key="'ram_'+index"
                                v-on:click="item.action"
                            >

                                    <v-list-item-content>

                                        <v-list-item-title>


                                         {{item.text}}

                                            <m-menu-chip
                                                v-if="item.badge_new"
                                            />

                                         </v-list-item-title>

                                    </v-list-item-content>

                            </v-list-item>

                        </v-list>

                    </v-menu>

                    <v-menu
                        bottom
                        offset-y
                    >

                    <template v-slot:activator="{ on }">

                        <v-btn
                            class="mx-2" fab dark small color="primary"

                            :disabled="has_sent"
                            v-on="on"
                        >
                            <v-icon dark>mdi-dots-vertical</v-icon>
                        </v-btn>


                    </template>



                    <v-list>
                        <v-list-item
                            v-on:click="onClickStartImportWinePulse()"
                            v-if="$rc.inIndustry('wine')"
                        >
                            <v-list-item-title>Add recipients from WinePulse report...</v-list-item-title>
                        </v-list-item>


                        <v-divider
                            v-if="$rc.inIndustry('wine')"
                        />

                        <template
                            v-if="this.$rc.$state.isAuthActorSystemAdmin() && bulk_message.is_demo_organization"
                        >
                            <m-restricted-menu-list-item
                                v-on:click="onClickAddDummyRecipients()"
                                text="Add dummy recipients..."
                            />

                            <m-restricted-menu-list-item
                                v-on:click="onClickAddDummySpamRecipients()"
                                text="Add dummy (spam) recipients..."
                            />

                            <v-divider/>
                        </template>

                        <template
                            v-if="this.$rc.$state.isCommerce7Enabled()"
                        >
                            <v-list-item
                                v-on:click="onClickRemoveRecipientsFromRecentOrders()"
                            >
                                <v-list-item-title>Remove recipients that placed a recent order...</v-list-item-title>
                                <m-menu-chip/>
                            </v-list-item>

                            <m-restricted-menu-list-item
                                v-on:click="onClickAddNotCustomClubShipment()"
                                v-if="$rc.$state.isAuthActorSystemAdmin()"
                                text="Add recipients who have not customized their club shipment..."
                            >
                                <m-menu-chip/>
                            </m-restricted-menu-list-item>

                            <v-divider/>
                        </template>

                        <template
                            v-if="bulk_message.is_sentiment_forecast_available"
                        >
                            <v-list-item
                                v-on:click="onClickOptimizeRecipients(false)"
                            >
                                <v-list-item-title>RedChirp AI:  Optimize Recipients...</v-list-item-title>
                                <m-menu-chip/>
                            </v-list-item>

                            <v-divider/>
                        </template>

                        <v-list-item
                            v-on:click="onEditErrorRateBeforeCancel()"
                        >
                            <v-list-item-title>Adjust max error rate...</v-list-item-title>
                        </v-list-item>

                        <v-list-item
                            v-on:click="deleteAllRecipients()"
                        >
                            <v-list-item-title>Delete all recipients...</v-list-item-title>
                        </v-list-item>

                    </v-list>

                    </v-menu>

                </div>

                <m-edit-bulk-message-filters
                    :bulk_message="bulk_message"
                    v-on:patch="onPatchFilter"
                />


                <div
                    v-show="!bulk_message.is_archived"
                >

                    <div
                        style="margin-top: 25px"
                        v-show="!has_sent"
                    >
                        <h2>Send</h2>
                    </div>

                        <m-edit-select-employee
                            :organization_id="bulk_message.organization_id"
                            v-model="bulk_message.notification_employee_ids"
                            v-on:change="upsertBulkMessage"
                            label="Users to notify when message is sent (optional)"
                            multiple
                        />

                        <m-legal-checkbox-agreement
                            type="bulk-message-attestation"
                            :organization_id="this.bulk_message.organization_id"
                            v-model="consent_acknowledgement"
                            ref="bulk_message_attestation"
                            v-show="!has_sent && !is_scheduled_but_unsent"
                        />

                        <v-checkbox
                            label="I have tested my message and am happy with the message and any attachments"
                            v-model="tested_message"
                            style="margin-right: 15px"
                            v-show="!has_sent && !is_scheduled_but_unsent"
                        />

                        <p
                            v-show="!has_sent && !is_scheduled_but_unsent"
                        >
                            <a href="https://vimeo.com/650475431" target="_blank">How to test your messages...</a>
                        </p>

                        <v-list
                            v-if="shouldDisable() && !has_sent"
                            dense
                        >
                            <v-subheader>To send the bulk message, please fix the following:</v-subheader>

                            <v-list-item
                                v-for="(item, index) in this.reasonsPreventingBulkMessageSend()"
                                :key="index"
                            >
                                <v-list-item-subtitle>
                                    {{item.text}}
                                    <a
                                        v-if="item.help_text"
                                        v-on:click="showHelpAlert(item)">
                                        help
                                    </a>
                                </v-list-item-subtitle>
                            </v-list-item>
                        </v-list>


                        <m-edit-instant
                            label="Scheduled Send Time"
                            v-model="bulk_message.scheduled_send_time"
                            :timezone="this.$rc.$state.getPrimaryTimezone()"
                            v-on:input="onScheduleTime"
                            v-show="bulk_message.scheduled_send_time != null"
                            ref="edit-scheduled-send-time"
                            style="margin-bottom: 15px"
                        />

                        <v-btn
                            color="primary"
                            v-on:click="onClickSendBulkMessage"
                            :disabled="shouldDisable()"
                            v-show="!has_sent && bulk_message.scheduled_send_time == null && !bulk_message.is_archived"
                            style="margin-right: 15px"
                        >
                            Send Bulk Message Now
                        </v-btn>

                        <v-btn
                            :disabled="shouldDisable()"
                            style="margin-right: 15px"
                            v-on:click="onClickScheduleSendInAdvance"
                            v-if="!has_sent && bulk_message.scheduled_send_time == null && !bulk_message.is_archived"
                        >
                            Schedule Send in Advance
                        </v-btn>

                        <v-btn
                            v-if="bulk_message.scheduled_send_time != null"
                            style="margin-right: 15px"
                            v-on:click="onClickCancelScheduledSend"
                            color="primary"
                        >
                            Cancel Scheduled Send
                        </v-btn>

                        <div
                            style="height: 30px"
                        />
                </div>


        </v-form>



        <m-bulk-outcome-change-dialog
            ref="change-outcome-dialog"
            v-on:ok="onChangeOutcome"
        />

        <m-preview-bulk-recipients-dialog
            ref="preview-bulk-recipients"
            v-on:ok="afterCommittingExcelChanges"
        />

        <m-bulk-outcome-create-dialog
            ref="create-outcome-dialog"
            v-on:ok="onCreateOutcome"
        />

        <m-bulk-recipient-edit-dialog
            ref="edit-bulk-recipient"
            v-on:ok="onSaveRecipient"
        />

        <m-model-r1-optimize-recipients-dialog
            ref="m-model-r1-optimize-recipients-dialog"
            v-on:ok="updateRecipients"
        />

        <m-bulk-message-add-recipients-yet-to-customize-club-dialog
            ref="m-bulk-message-add-recipients-yet-to-customize-club-dialog"
            v-on:ok="onOkAddRecipientsYetToCustomizeClubDialog"
        />

        <m-bulk-recipient-custom-message-dialog
            ref="custom-message-dialog"
            v-on:ok="updateCustomMessageForRecipient"
            hint="The custom message will override the bulk message text for this recipient only."
        />

        <m-bulk-recipient-preview-message-dialog
            ref="preview-message-dialog"
        />

        <m-bulk-message-progress-dialog
            ref="bulk-message-send-progress-dialog"
            v-on:completed="updateRecipients"
        />

        <m-message-details-dialog
            ref="message-details-dialog"
        />

        <m-send-test-message-dialog
            ref="send-test-message-dialog"
            v-on:send-test-message="onSendTestMessage"
        />

        <m-autocomplete-phone-number
            ref="add-phone-numbers"
            dialog_only
            :default_multiselect="true"
            action_button_text="Add Recipients"
            v-on:start-group-text="onAddMultiplePhoneNumbers"
        />

        <m-bulk-message-attachment-warning-dialog
            ref="bulk-message-attachment-warning-dialog"
        />

        <m-edit-select-dialog
            ref="select-list"
            v-on:ok="onAddAllListMembers($event)"
            multiple
        />

        <m-bulk-message-template-pick-dialog
            ref="bulk-message-template-pick-dialog"
            v-on:ok="onUseBulkMessageTemplate"
        />

        <m-bulk-message-search-imports-dialog
            ref="bulk-message-search-imports-dialog"
        />


        <span style="display: inline-flex; align-items: center;">
            <v-btn
                v-if="has_sent"
                style="margin-right: 10px;"
                v-on:click="onClickOrderAttribution"
            >
                Order Attribution...
            </v-btn>

            <m-restricted-button
                v-if="this.$rc.$state.isAuthActorSystemAdmin()"
                v-on:click="onClickOptimizeRecipients(true)"
            >
                Model R1 Access
            </m-restricted-button>
        </span>

    <m-edit-blobs-dialog
        ref="import-wine-pulse-report-dialog"
        v-on:ok="onImportWinePulseReport"
        :externally_visible="false"
        :accept="['csv']"
        :allow_multiple="false"
        purpose="excel-import"
        title="Import WinePulse Report"
        :winepulse="true"
    />

    </v-container>


</m-pageframe>
</template>

<script>
import Time from '@/js/time.js';
import E164 from '@/js/E164.js';
import { v4 as uuidv4 } from 'uuid';
import _ from 'lodash';
import BulkMessageSendStatusMixin from '@/mixins/bulk-message-send-status.js';
import ProvisionWarningBulkMessages from '@/components/bulk/provision-warning-bulk-messages.vue';

export default
{
    mixins: [BulkMessageSendStatusMixin],

    components: {
        ProvisionWarningBulkMessages
    },

    data() {
        return {
            bulk_message: null,

            recipients: [],
            table_options: null,
            result_count: 0,

            loading_recipients: false,

            use_outcome_tracking: null,
            consent_acknowledgement: false,
            tested_message: false,
            advanced_settings_menu: false,
            outcomes: [],
            recipient_extra_fields: [],
            full_text: null,

            outcome_tracking_items: [
                {'text': 'Do not use outcome tracking', 'value': 'no' },
                {'text': 'Use outcome tracking', 'value': 'yes' },
            ],

            rules: {
                no_pay_links: (value) => {
                    let normalized_text = value.toLowerCase().trim();
                    if ( normalized_text.indexOf('redchirp.com/app/pay/') >= 0 )
                    {
                        return 'Message cannot contain pay links: https://app.redchirp.com/app/pay';
                    }
                    return true;
                },
            },

            message_type_choices: [
                {'text' : 'Informational', 'value' : 'informational' },
                {'text' : 'Promotional', 'value' : 'promotional' }
            ],

            interval: null,
            current_example_message: null,
        }
    },

    mounted () {
        this.update();
        this.interval = window.setInterval(this.getSendStatus, 3000);
    },

    beforeDestroy ()
    {
        if ( this.interval != null )
        {
            window.clearInterval(this.interval);
            this.interval = null;
        }
    },


    methods: {

        debounceUpdateRecipients: _.debounce(function(){
            this.updateRecipients();
        }, 751),


        updateRecipients()
        {
            this.loading_recipients = true;

            this.$rc.rest({
                'spinner': false,
                'endpoint': 'LIST: /api/v1/bulk/recipients/search',

                'params' : {
                        ...this.$rc.parseVTableOptions(this.table_options, 'bulk_message_recipient_id:asc|sent_time:desc'),
                        'full_text': this.full_text,
                        'organization_id' : this.bulk_message.organization_id,
                        'bulk_message_id' : this.bulk_message.bulk_message_id,
                    },

                'on_success': (data) => {

                        this.recipients = data.results;
                        this.result_count = data.result_count;
                        this.recipient_extra_fields = data.recipient_extra_fields;
                        this.loading_recipients = false;
                    },

                'on_error': () => {
                    this.loading_recipients = false;
                    this.$rc.alert('Error','Unable to list recipients for this bulk message');
                },
            });
        },

        update()
        {
            this.$rc.rest(
            'GET: /api/v1/bulk/messages/get-profile/:bulk_message_id',

            {
                'bulk_message_id': this.$route.params.bulk_message_id,
            },

            (data) => {
                this.bulk_message = data;
                this.updateRecipients();
                this.updateOutcomes();

                if ( this.bulk_message )
                {
                    if ( this.bulk_message.use_outcome_tracking )
                    {
                        this.use_outcome_tracking = 'yes';
                    }
                    else
                    {
                        this.use_outcome_tracking = 'no';
                    }
                }
                this.getSendStatus();
            },

            () => {
                this.$rc.alert('Error','Unable to get bulk message');
            },
        );
        },

        onClickOptimizeRecipients(read_only)
        {
            this.$refs['m-model-r1-optimize-recipients-dialog'].showDialog(this.bulk_message.recipient_count, read_only);
        },

        prettyFirstName(item)
        {
            if ( item.recipient_contact_first_name != null )
                return item.recipient_contact_first_name;

            if ( item.extra_fields.first_name != null )
                return item.extra_fields.first_name;

            return null;
        },

        prettyLastName(item)
        {
            if ( item.recipient_contact_last_name != null )
                return item.recipient_contact_last_name;

            if ( item.extra_fields.last_name != null )
                return item.extra_fields.last_name;

            return null;
        },

        getSendStatus()
        {
            if ( this.bulk_message == null )
                return;


            this.mixinGetSendStatusDetail(
                this.bulk_message.bulk_message_id,
                (data) => {
                    this.bulk_message.state = data.state; // update the state in real time
                    this.$refs['bulk-message-send-progress-dialog'].updateProgress(data, this.bulk_message.from_endpoint);
                }
            );
        },

        updateBulkMessageLabel(new_label)
        {
            this.bulk_message.label = new_label;
            this.upsertBulkMessage();
        },

        updateBulkMessageType(new_type)
        {
            this.bulk_message.type = new_type;
            this.upsertBulkMessage();
        },

        onToggleDoubleOptIn()
        {
            this.upsertBulkMessage();
        },

        updateOldBulkMessageText(new_value)
        {
            this.bulk_message.message_patterns = [new_value.trim()];
            this.upsertBulkMessage();
        },

        onPatchFilter(patch)
        {
            this.bulk_message = {
                ... this.bulk_message,
                ... patch
            };

            this.$rc.showSpinner();

            this.$rc.putBulkMessage(
                this.bulk_message,
                (data) => {
                    this.$rc.hideSpinner();
                    this.update();
                },
            );
        },

        upsertBulkMessage()
        {
            this.$rc.showSpinner();

            this.$rc.putBulkMessage(
                this.bulk_message,
                (data) => {
                    this.$rc.hideSpinner();
                },
            );
        },

        onExampleMessageChanged(example_message) {
            this.current_example_message = example_message;
        },

        selectTeamEndpoint(endpoint)
        {
            this.bulk_message.from_endpoint = endpoint;
            this.upsertBulkMessage();
        },

        showHelpAlert(item)
        {
            this.$rc.alert(
                'Unable to send Bulk Message',
                item.help_text,
            );
        },

        allTemplatePlaceholders(message)
        {
            let messages = Array.isArray(message) ? message : [message];

            let ret = [];

            for ( let i = 0; i < messages.length; i++ )
            {
                let cur_message = messages[i];
                let m = cur_message.match(/{placeholder.+}/g);
                if ( m == null ) m = [];

                for ( let j = 0; j < m.length; j++ )
                {
                    if ( !ret.includes(m[j]) )
                        ret.push(m[j]);
                }
            }

            return ret;
        },

        reasonsPreventingBulkMessageSend()
        {
            let ret = [];

            if ( this.$rc.$state.profile?.provision_info?.bulk_message_send_disabled_message != null )
            {
                ret.push(
                    {
                        'text' : 'Sending bulk messages has been disabled for your organization.',
                        'help_text' : this.$rc.$state.profile?.provision_info?.bulk_message_send_disabled_message
                    }
                );

            }

            if ( this.$rc.$state.profile.employee_account.bulk_message_permissions != 'send-bulk-messages' )
            {
                ret.push(
                    { 'text' : 'Insufficient permissions to send bulk message.',
                        'help_text' : 'To enable sending bulk messages, ask an admin of your organization to update your user permissions. These can be changed in Organization Settings > RedChirp Users > Action > Set Bulk Message Permissions.' }
                );
            }

            if ( this.bulk_message )
            {
                if ( this.use_old_message_patterns && this.old_message_text == null )
                {
                    ret.push({ 'text' : 'Message Text cannot be blank.'});
                }

                if ( this.bulk_message.from_endpoint == null || this.bulk_message.from_endpoint == '' )
                {
                    ret.push({ 'text' : 'Must select a phone number.'});
                }
            }

            if ( this.bulk_message.template_id != null )
            {
                let placeholders = this.allTemplatePlaceholders(this.bulk_message.message_patterns);

                placeholders.forEach((item) => ret.push({ 'text' : 'Your message contains a placeholder variable: '+item}));
            }

            let is_mms = this.message_type.startsWith('MMS') || this.bulk_message.blob_ids.length > 0;
            let max_pattern_length = this.$rc.codePointLength(this.bulk_message.message_patterns.reduce((l, r) => l.length > r.length ? l : r, ''));

            if (is_mms && max_pattern_length > 1550) // MMS 1600, with 50 char for variable substitution buffer
            {
                ret.push(
                    {
                        'text' : 'Message text is too long (max 1550)',
                        //'help_text' : 'The MMS protocol has a maximum message length of 1600 characters. RedChirp reserves 50 characters to accommodate variable substitution during message generation.'
                    }
                )
            }

            if ( this.recipients == null || this.recipients.length == 0 )
            {
                ret.push({ 'text' : 'Needs to have at least one recipient.'});
            }

            if ( !this.consent_acknowledgement && !this.is_scheduled_but_unsent )
            {
                ret.push({ 'text' : 'Need to check the affirmation check box.'});
            }

            if ( !this.tested_message && !this.is_scheduled_but_unsent )
            {
                ret.push({ 'text' : 'Need to confirm that you have tested the message.'});
            }

            let has_at_least_one_unsent = false;

            for ( let i=0; i < this.recipients.length; i++ )
            {
                let recipient = this.recipients[i];

                // if they haven't received a text, ensure they're not opted out
                if ( recipient.has_opted_out_of_bulk_text )
                {
                    continue;
                }

                if ( recipient.sent_time == null || recipient.sent_time == 0 )
                {
                    has_at_least_one_unsent = true;
                    break;
                }
            }

            if ( !has_at_least_one_unsent )
            {
                ret.push({ 'text' : 'Need at least one new recipient to send this message again.'});
            }

            {
                let bad_vars = this.listVariablesUsedInMessageThatDontExist();

                for ( let i = 0; i < bad_vars.length; i++ )
                {
                    ret.push({'text' : bad_vars[i]});
                }
            }
            
            //Check if message type is not informational and double opt-in is enabled
            if (this.bulk_message.is_double_opt_in_message && this.bulk_message.category !== 'informational') {
                ret.push({'text': 'Double opt-in messages should be sent with the informational message type.'});
            }

            if (this.bulk_message.before_send_add_list_ids?.length > 0)
            {
                ret.push({ 'text' : 'Clear all selections from "Before sending, create recipients... from list".'});
            }

            return ret;
        },

        listVariablesUsedInMessageThatDontExist()
        {
            if ( this.bulk_message == null ) return [];
            if ( !this.use_old_message_patterns ) return [];
            if ( this.old_message_text == null  ) return [];

            let value = this.removeURLS(this.old_message_text);
            let ret = [];

            while(value.length > 0)
            {
                let start_idx = value.indexOf('%');
                if ( start_idx == -1 || start_idx >= value.length-1 ) break;

                value = value.substring(start_idx + 1);

                let end_idx = value.indexOf('%');

                if ( end_idx == -1 )
                    break;

                let cur_var = value.substring(0, end_idx);

                let found_var = false;

                for ( let i = 0; i < this.message_variables.length; i++ )
                {
                    if ( this.message_variables[i].value.toLowerCase() == ('%'+cur_var+'%').toLowerCase() )
                        found_var = true;
                }

                if ( cur_var.length == 0 )
                    found_var = true; // they empty variable (i.e. %% is used to encode  single % safely -- it exists, therefore)

                if ( !found_var )
                {
                    ret.push('You are using an undefined variable, %'+cur_var+'%'+', in your message');
                }

                value = value.substring(end_idx+1);
            }

            return ret;
        },

        removeURLS(value)
        {
            let longestURL = (str) =>
            {
                let words = str.split(" ");

                let ret = null;

                for ( let i = 0; i < words.length; i++ )
                {
                    let word = words[i];
                    if ( word.toLowerCase().startsWith('http://') || word.toLowerCase().startsWith('https://') )
                    {
                        if ( ret == null || ret.length < word.length )
                            ret = word;
                    }
                }

                return ret;
            }

            while(value.length > 0)
            {
                let longest_url = longestURL(value);
                if ( longest_url == null ) break;
                value = value.replace(longest_url,'');
            }

            return value;
        },

        shouldDisable()
        {
            if ( this.reasonsPreventingBulkMessageSend().length > 0 )
            {
                return true;
            }

            return false;
        },

        onUseBulkMessageTemplate(template)
        {
            this.bulk_message.message_patterns = template.message_patterns;
            this.bulk_message.template_id = template.template_id;
            this.$rc.snackbar('Message has been updated: '+template.label);
            this.upsertBulkMessage();
        },

        onClickSendBulkMessage()
        {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/bulk/schedule-warnings',

                'params' : {
                        'bulk_message_id': this.bulk_message.bulk_message_id,
                        'time': null, // meaning, now
                    },

                'on_success': (data) => {

                        if ( data.warning == null )
                        {
                            this.afterWarningsOnClickSendBulkMessage();
                            return;
                        }
                        this.$rc.alert(
                            data.warning.title,
                            data.warning.body_html,
                            () => {}
                        );
                    },

                'on_error': 'Unable to check for scheduling time warnings',
            });
        },

        afterWarningsOnClickSendBulkMessage()
        {
            this.$rc.confirm(
                'Final Confirmation: Send message',
                'This is a final confirmation that you are ready to send this message to <b>'+this.result_count+' recipients.</b><br/><br/>Once you press send <b>RedChirp will start sending this message to the recipients you have specified.</b>',
                [
                    {'text' : 'Cancel', 'value' : 'cancel' },
                    {'text' : 'Send', 'value' : 'confirm' },
                ],
                (response) => {
                    if ( response != 'confirm' ) return;
                    this.sendUnsentBulkMessages();
                },
            );
        },

        onClickScheduleSendInAdvance()
        {
            this.$refs['edit-scheduled-send-time'].onClickEdit();
        },

        sendUnsentBulkMessages()
        {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/bulk/messages/send-unsent',

                'params' : {
                        'bulk_message_id': this.bulk_message.bulk_message_id,
                        'specimen_bulk_message_attestation' : this.$refs['bulk_message_attestation'].getSpecimen(),
                    },

                'on_success': (data) => {

                        if ( data.attestation_signature_error_message != null )
                        {
                            this.$rc.alert('Unable to send bulk message', data.attestation_signature_error_message);
                            return;
                        }

                        this.bulk_message.sent_time = Date.now(); // forces has_sent to be true which disables all editing
                        this.$refs['bulk-message-send-progress-dialog'].updateProgress(data, this.bulk_message.from_endpoint);

                    },

                'on_error': 'Unable to send bulk message.',
            });
        },



        onCreateOutcome(outcome_id, label)
        {
            // verify the label isn't already being used (no duplicate labels)
            for ( let i = 0; i < this.outcomes.length; i++ )
            {
                if ( label.toLowerCase() === this.outcomes[i].label.toLowerCase() )
                {
                    this.$rc.alert(
                        'Outcome Already Exists',
                        'There already exists an outcome with the name: ' + label + '. Names must be unique.',
                    );

                    return;
                }
            }

            let outcome = {
                'organization_id' : this.bulk_message.organization_id,
                'bulk_message_id' : this.bulk_message.bulk_message_id,
                'outcome_id' : outcome_id,
                'label' : label,
            };

            this.$rc.upsertBulkMessageOutcome(
                outcome,
                (data) => {
                    this.updateOutcomes();
                },
                () => {
                    this.$rc.alert(
                        'Error Creating Outcome',
                        'There was an error creating the outcome. Please try again.'
                    );
                }
            );
        },

        onClickEditRecipient(recipient)
        {
            this.$refs['edit-bulk-recipient'].showDialog(
                this.bulk_message.organization_id,
                this.bulk_message.bulk_message_id,
                recipient
            );
        },

        onPreviewBulkRecipients(recipients)
        {
            this.$refs['preview-bulk-recipients'].showDialog(
                this.bulk_message.organization_id,
                this.bulk_message.bulk_message_id,
                recipients
            );
        },

        afterCommittingExcelChanges()
        {
            this.updateRecipients();
        },

        onClickCreateRecipient()
        {
            this.$refs['edit-bulk-recipient'].showDialog(
                this.bulk_message.organization_id,
                this.bulk_message.bulk_message_id,
                null
            );
        },

        onClickAddContacts()
        {
            this.$refs['add-phone-numbers'].showDialog();
        },

        onAddMultiplePhoneNumbers(items)
        {
            let endpoints = [];

            for ( let i = 0; i < items.length; i++ )
            {
                endpoints.push(items[i].value);
            }

            let params = {
                'bulk_message_id' : this.$route.params.bulk_message_id,
                'endpoints' : endpoints
            };

            this.$rc.showSpinner();

            this.$rc.rest(
                'POST: /api/v1/bulk/recipients/put-contacts',

                params,

                (data) => {
                    this.$rc.hideSpinner();

                    this.updateRecipients();
                },

                () => {
                    this.$rc.hideSpinner();
                    this.$rc.alert('Error','Unable to add recipients');
                },
            );
        },

        onClickExcelImport()
        {
            let import_id = uuidv4();

            this.$router.push({
                'name': 'bulk-recipient-import',
                'params' : {
                    'organization_id' : this.bulk_message.organization_id,
                    'bulk_message_id' : this.bulk_message.bulk_message_id,
                    'import_id' : import_id,
                }
            }).catch(e=>{}); // swallow any exception
        },

        deleteAllRecipients()
        {
            this.$rc.confirm(
                'Delete All Recipients',
                'Are you sure you want to delete all recipients? This action cannot be undone.',
                [
                    {'text': 'Cancel', 'value': 'cancel'},
                    {'text': 'Confirm', 'value': 'confirm'},
                ],
                (response) => {

                    let search = {
                        'organization_id' : this.bulk_message.organization_id,
                        'bulk_message_id' : this.bulk_message.bulk_message_id,
                    };

                    if (response == 'confirm') {
                        this.$rc.deleteBulkMessageRecipients(
                            search,
                            (data) => {
                                this.updateRecipients();
                            },
                            () => {
                                this.$rc.alert(
                                    'Error Deleting Recipients',
                                    'There was an error deleting the recipients, please try again.'
                                );
                            }
                        );
                    } else {
                        return;
                    }
                }
            );
        },

        onEditErrorRateBeforeCancel()
        {
            this.$rc.confirm({
                'title': 'Warning: Max Error Rate',
                'body': 'Sending a bulk message with a high error rate could incur significant charges for messages that do not reach your recipients. Are you sure you want to edit this setting?',
                'btn_text_ok': 'Adjust Error Rate',
                'btn_text_cancel': 'Cancel',
                'on_ok': () => {
                    this.$rc.prompt({
                        'title' : 'Max error rate setting',
                        'label' : 'If more than 100 messages have been sent and more than this percentage of recipients return an error, this bulk message will be automatically cancelled',
                        'value' : this.bulk_message.max_error_percent_before_cancel,
                        'is_valid': this.$rc.validationRuleInteger(0, 100),
                        'after_prompt' : (value) => {
                            this.bulk_message.max_error_percent_before_cancel = value;
                            this.upsertBulkMessage();
                        }
                    });
                }
            });
        },

        onSaveRecipient()
        {
            this.updateRecipients();
        },

        onClickCreateOutcome()
        {
            let outcome_id = uuidv4();
            this.$refs['create-outcome-dialog'].showDialog(outcome_id);
        },

        afterDeleteOutcome(event, outcome)
        {
            this.$rc.rest({
                'spinner': true,
                'endpoint': 'DELETE: /api/v1/bulk/messages/outcome',

                'params' : {
                        'bulk_message_id' : outcome.bulk_message_id,
                        'outcome_id' : outcome.outcome_id,
                    },

                'on_success': (data) => {

                        if ( data.failed_in_use )
                        {
                            this.$rc.alert(
                                'Unable to Delete Outcome',
                                'Outcomes that are in use cannot be deleted. Remove the usages of this outcome in order to delete it.'
                            );
                        }

                        this.updateOutcomes();
                    },

                'on_error': 'Unable to delete outcome',
            });
        },

        afterEditOutcome(label_change, outcome)
        {
            outcome.label = label_change;

            this.$rc.upsertBulkMessageOutcome(
                outcome,
                (data) => {
                    this.updateOutcomes();
                },
                () => {
                    this.$rc.alert('Error Updating Outcome', 'There was an error updating the outcome, please try again.');
                },
            );
        },

        updateOutcomes()
        {
            if ( this.bulk_message )
            {
                let search = {
                    'bulk_message_id' : this.bulk_message.bulk_message_id,
                };

                this.$rc.listBulkMessageOutcomes(
                    search,
                    (data) => {
                        this.outcomes = data.results;
                    },
                    () => {
                        this.$rc.alert(
                            'Error Retrieving Outcomes',
                            'There was an error retrieving outcomes for this message. Please try again.'
                        );
                    },
                );
            }
        },

        updateOutcomeTracking(new_value)
        {
            switch( new_value )
            {
                case 'yes':
                {
                    this.bulk_message.use_outcome_tracking = true;
                    break;
                }
                case 'no':
                {
                    this.bulk_message.use_outcome_tracking = false;
                    break;
                }
            }

            this.upsertBulkMessage();
        },

        onClickMessageGenerationTechnicalDetails(item)
        {
            this.$rc.copy_code_dialog({
                'title': 'Message Generation Technical Details',
                'code': item.message_generation_technical_details,
            });
        },

        previewMessage(recipient)
        {
            if ( this.use_old_message_patterns && this.listVariablesUsedInMessageThatDontExist().length > 0 )
            {
                let message = '';
                let vars = this.listVariablesUsedInMessageThatDontExist();

                for ( let i = 0; i < vars.length; i++ )
                {
                    message += vars[i];
                    message += '<br/><br/>';
                }


                message += 'You will not be able to send or preview your message until you remove the undefined variables from your message';

                this.$rc.alert('Your message uses undefined variables',message);
                return;
            }

            this.$rc.rest({
                'spinner': true,
                'endpoint': 'PUT: /api/v1/bulk/messages/preview',

                'params' : {
                        'bulk_message_id': this.bulk_message.bulk_message_id,
                        'bulk_recipient_id': recipient.bulk_message_recipient_id,
                    },

                'on_success': (data) => {
                        this.$refs['preview-message-dialog'].showDialog(data.message, this.bulk_message.blob_ids, data.technical_details);
                    },

                'on_error': 'Unable to create preview message',
            });
        },

        selectTestMessageRecipient(recipient)
        {
            if ( this.bulk_message.from_endpoint == null )
            {
                this.$rc.alert('Unable to send test message', 'You need to select the phone number that will be used to send the bulk message message <b>before</b> you can send a test message.');
                return;
            }

            if ( this.old_message_text == null )
            {
                this.$rc.alert('Unable to send test message', 'You need to enter the message you would like to send <b>before</b> you can send a test message.');
                return;
            }

            this.$refs['send-test-message-dialog'].showDialog(
                recipient.bulk_message_recipient_id,
                recipient.recipient_contact_first_name,
                recipient.recipient_contact_last_name
            );
        },

        onSendTestMessage(recipient_id, endpoint, prepend_test)
        {
            this.$rc.rest({
                'spinner': true,
                'endpoint': 'SEND: /api/v1/bulk/messages/send-test',
                'timeout': 30000,

                'params' : {
                        'bulk_message_id': this.bulk_message.bulk_message_id,
                        'bulk_message_recipient_id': recipient_id,
                        'to_endpoint': endpoint,
                        'prepend_test': prepend_test,
                    },

                'on_success': (data) => {
                        this.$rc.alert(
                            'Message Sent',
                            'Test message was successfully sent',
                        );
                    },

                'on_error': 'There was an error sending the test message. Please try again.',
            });
        },

        editCustomMessage(recipient)
        {
            this.$refs['custom-message-dialog'].showDialog(recipient.bulk_message_recipient_id, recipient.custom_message);
        },

        updateCustomMessageForRecipient(recipient_id, new_message)
        {
            if ( new_message ) new_message = new_message.trim();

            this.$rc.updateBulkRecipientCustomMessage(
                recipient_id,
                new_message,
                (data) => {
                    this.updateRecipients();
                },
            );
        },

        changeOutcome(recipient)
        {
            let search = {
                'bulk_message_id' : this.bulk_message.bulk_message_id,
            };

            this.$rc.listBulkMessageOutcomes(
                search,
                (data) => {
                    let outcomes = [{ 'text' : 'Not Selected', 'value' : null}];

                    for ( let i = 0; i < data.results.length; i++ )
                    {
                        outcomes.push({
                            'text' : data.results[i].label,
                            'value' : data.results[i].outcome_id,
                        });
                    }

                    this.$refs['change-outcome-dialog'].showDialog(
                        recipient.bulk_message_recipient_id,
                        recipient.outcome_id,
                        outcomes
                    );
                }
            );
        },

        deleteRecipient(recipient)
        {
            if ( recipient.sent_time != null )
            {
                this.$rc.alert(
                    'Unable to Delete Recipient',
                    'This recipient has already received the bulk message and cannot be deleted.'
                );

                return;
            }

            this.$rc.confirm(
                'Delete Recipient',
                'Are you sure you want to delete this recipient?',
                [
                    {'text': 'Cancel', 'value': 'cancel'},
                    {'text': 'Confirm', 'value': 'confirm'},
                ],
                (response) => {

                    if (response == 'confirm') {
                        this.$rc.deleteBulkMessageRecipient(
                            recipient.bulk_message_recipient_id,
                            (data) => {
                                this.updateRecipients();
                            },
                            () => {
                                this.$rc.alert(
                                    'Error Deleting Recipient',
                                    'There was an error deleting the recipient. Please try again.'
                                );
                            },
                        );
                    } else {
                        return;
                    }
                }
            );
        },

        onChangeOutcome(bulk_recipient_id, new_outcome_id)
        {
            this.$rc.updateBulkRecipientOutcome(
                bulk_recipient_id,
                new_outcome_id,
                (data) => {
                    this.updateRecipients();
                },
                () => {
                    this.$rc.alert(
                        'Error Setting Outcome',
                        'There was an error setting the outcome for the recipient. Please try again.'
                    );
                }
            );
        },

        onRetryMessage(item)
        {
            this.$rc.confirm(
                'Confirm Retry',
                'Retry sending the bulk message to this recipient?',
                [
                    {'text' : 'Cancel', 'value' : null },
                    {'text' : 'Send', 'value' : 'confirm' },
                ],
                (response) => {
                    if (response == 'confirm') {
                        this.$rc.showSpinner();
                        this.$rc.resendMessage(
                            item.stored_message_id,
                            (data) => {
                                this.$rc.alert(
                                    'Message Sent',
                                    'The bulk message was sent successfully',
                                    () => {
                                        this.updateRecipients();
                                    }
                                );
                            },
                            () => {
                                this.$rc.hideSpinner();
                                this.$rc.alert(
                                    'Message Failed to Send',
                                    'Retry failed to send, check the Message Details and try again',
                                );
                            }
                        );
                    } else {
                        return;
                    }
                },
            );
        },

        onMessageDetails(item)
        {
            this.$refs['message-details-dialog'].showDialog(item.stored_message_id);
        },

        onViewConversation(item)
        {
            this.$rc.getConversationFromBulkRecipient(
                item.conversation_id,
                item.recipient_endpoint,
                (data) => {
                    this.$rc.toHomeForEndpoint(this.$router, data.team_endpoint, true, data.conversation_definition.conversation_id);
                },

                () => {
                    this.$rc.alert(
                        'Error Retrieving Conversation',
                        'There was an error retrieving the conversation: ' + item.conversation_id,
                    );
                },
            );
        },

        showViewConversation(item)
        {
            if ( item != null && item.stored_message_state == 'delivered' ) return true;

            return false;
        },

        showViewMessageDetails(item)
        {
            if ( item != null && item.state != null ) return true;

            return false;
        },

        showRetryMessage(item)
        {
            if ( item != null && item.state == 'error'
                && this.$rc.$state.profile.employee_account.bulk_message_permissions == 'send-bulk-messages') return true;

            return false;
        },

        exportToExcel()
        {
            this.$rc.excelExport(
                    'EXPORT: /api/v1/bulk/recipients/export/:organization_id/:bulk_message_id',
                    {
                        'organization_id': this.bulk_message.organization_id,
                        'bulk_message_id': this.bulk_message.bulk_message_id,
                    }
                );
        },



        prettyReplied(item)
        {
            if ( !item.sent_time ) return '';

            if ( item.replied ) return 'Yes';

            return 'No';
        },

        prettyStoredMessageState(item)
        {
            if ( item.opt_out_state == 'opt-out' )
            {
                return 'Opted Out';
            }

            if ( item.state == 'cancelled' )
            {
                return 'Cancelled';
            }

            let state = item.state;

            if ( state == null || state == '' || state == 'pending' ) return '';

            if ( state == 'sent' )
            {
                state = item.stored_message_state;

                if ( state == null || state == '' ) return '';

                if ( state == 'delivered' ) return 'Delivered';
                if ( state == 'in-transit' ) return 'In Transit';
                if ( state == 'error' ) return 'Error';
            }

            if ( state == 'filtered' )
            {
                return 'Filtered';
            }

            return 'Failed';
        },

        prettyCustomMessage(custom_message)
        {
            if ( custom_message == null || custom_message == '' ) return 'No';

            return 'Yes';
        },

        prettySentTime(item)
        {
            if ( item.not_sent_message != null )
                return item.not_sent_message;

            let time = item.sent_time;

            if ( time == null || time == '' )
                return '';

            return Time.getISODate(time) + ' ' + Time.getPeriodClockTime(time);
        },

        prettyPrintPhoneNumber(endpoint) {
            return E164.prettyPrintUsPhoneNumber(endpoint);
        },

        onClickOk() {
            this.$router.push({
                'name': 'search-consent-register',
                'params' : {
                    'organization_id' : this.organization_id,
                }
            }).catch(e=>{}); // swallow any exception
        },

        genericCompare(first, second, desc)
        {
            // nulls should be last
            if ( first == null && second != null ) return 1;
            if ( first != null && second == null ) return -1;
            if ( first == null && second == null ) return 0;

            if ( !desc ) {
                return first > second ? 1 : -1;
            } else {
                return second > first ? 1 : -1;
            }
        },

        customRecipientSort(items, headers, is_desc)
        {
            // descending information comes in through an array
            let desc = is_desc[0] ? true : false;

            // we don't support multi column, sort, so simply sort on the first header
            let index_header = headers[0];
            if ( !index_header ) return items;

            items.sort((a, b) => {
                if (index_header === "sent_time")
                {
                    return this.genericCompare(a.sent_time, b.sent_time, desc);
                }
                if (index_header === "pretty_recipient_endpoint")
                {
                    return this.genericCompare(a.recipient_endpoint, b.recipient_endpoint, desc);
                }
                if (index_header === "status")
                {
                    return this.genericCompare(a.state, b.state, desc);
                }
                if ( index_header === "replied" )
                {
                    return this.genericCompare(a.replied, b.replied, desc);
                }
                else
                {
                    return this.genericCompare(a[index_header], b[index_header], desc);
                }
            });

            return items;
        },

        onClickNewAIDetails()
        {
            window.open('https://redchirp.com/introducing-redchirps-ai-recipient-list-optimization/', '_blank');
        },

        onClickAddAllListMembers()
        {
            this.$rc.showSpinner();

            this.$rc.rest(
                'LIST: /api/v1/lists/choose-org-list',

                {
                    'organization_id' : this.$route.params.organization_id,
                    'include_auto_lists': true
                },

                (data) => {
                    this.$rc.hideSpinner();

                    this.$refs['select-list'].showDialog('Select List', null, data.results);
                },

                () => {
                    this.$rc.hideSpinner();
                    this.$rc.alert('Error','Unable to complete API call');
                },
            );
        },

        onClickAddCommerce7Tag()
        {
            this.$router.push({
                'name': 'crm-tag-meta-data',
                'params' : {
                    'organization_id': this.$route.params.organization_id,
                    'content_type': 'commerce7',
                },
                'query' : {
                    'add_to_bulk_message_id': this.$route.params.bulk_message_id,
                },
            });
        },

        onClickAddOffsetGroup()
        {
            this.$router.push({
                'name': 'crm-group-meta-data',
                'params' : {
                    'organization_id': this.$route.params.organization_id,
                    'integration_type': 'offset',
                },
                'query' : {
                    'add_to_bulk_message': this.$route.params.bulk_message_id,
                },
            });
        },

        onClickAddNotCustomClubShipment()
        {
            this.$refs['m-bulk-message-add-recipients-yet-to-customize-club-dialog'].showDialog();
        },

        onOkAddRecipientsYetToCustomizeClubDialog(club_release)
        {
            /*this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/bulk/add-dummy-recipients',

                'params' : {
                        'size': 723,
                        'bulk_message_id': this.$route.params.bulk_message_id,
                    },

                'on_success': (data) => {
                        this.update();
                    },

                'on_error': 'Unable to add club members that have not cusomized this shipment',
            }); */


            this.$rc.startProcess({
                'type': 'crm-create-excel-preview-of-customers-who-have-not-customized-club-release',

                'params': {
                    'organization_id' : this.$route.params.organization_id,
                    'bulk_message_id': this.add_to_bulk_message.bulk_message_id,
                    ... club_release
                },

                'title': 'Listing customers who have not customized club release',
                'finished_button_text': 'Preview Recipients',
                'failed_button_text': 'Cancel',

                status: (pte) => {
                    return 'Customer phone numbers: '+pte.progress_counter;
                },

                on_finished: (pte) => {
                    this.$router.push({
                        'name': 'bulk-recipient-preview',
                        'params' : {
                            'organization_id' : pte.output.organization_id,
                            'bulk_message_id' : pte.output.bulk_message_id,
                            'import_id' : pte.output.import_id
                        }
                    });
                },

                on_failed: (pte) => {
                },

            });

        },

        onClickAddCommerce7Club()
        {
             this.$router.push({
                'name': 'crm-club-meta-data',
                'params' : {
                    'organization_id': this.$route.params.organization_id,
                    'content_type': 'commerce7',
                },
                'query' : {
                    'add_to_bulk_message_id': this.$route.params.bulk_message_id,
                },
            });
        },

        onClickAddOffsetClub()
        {
             this.$router.push({
                'name': 'crm-club-meta-data',
                'params' : {
                    'organization_id': this.$route.params.organization_id,
                    'content_type': 'offset',
                },
                'query' : {
                    'add_to_bulk_message_id': this.$route.params.bulk_message_id,
                },
            });
        },

        promptForDummyRecipientCount(after_action)
        {
            this.$rc.prompt({
                'title' : 'Number of recipients',
                'label' : 'Number of recipients',
                'value' : '300',
                'after_prompt' : (value) => {
                    let size = parseInt(value);

                    if ( Number.isNaN(size) )
                        return;

                    after_action(size);
                }
            });
        },

        onClickDummyExport()
        {
            this.promptForDummyRecipientCount(
                (size) => {
                    this.$rc.excelExport(
                            'EXPORT: /api/v1/bulk/dummy-recipients',
                            {
                                'size': size,
                            }
                        );
                }
            );
        },

        onClickWinePulseDummy()
        {
            window.open('https://files.redchirp.com/ecwqreeducufuzsybbbcxtedvfyuwvse.csv','_blank');
        },



        onClickAddDummyRecipients()
        {
            this.promptForDummyRecipientCount(
                (size) => {
                    this.$rc.rest({
                        'spinner': false,
                        'endpoint': 'PUT: /api/v1/bulk/add-dummy-recipients',

                        'params' : {
                                'size': size,
                                'bulk_message_id': this.$route.params.bulk_message_id,
                            },

                        'on_success': (data) => {
                                this.update();
                            },

                        'on_error': 'Unable to add dummy recipients',
                    });


                }
            );
        },

        onClickAddDummySpamRecipients()
        {
            this.promptForDummyRecipientCount(
                (size) => {
                    this.$rc.rest({
                        'spinner': false,
                        'endpoint': 'PUT: /api/v1/bulk/add-dummy-recipients',

                        'params' : {
                                'size': size,
                                'bulk_message_id': this.$route.params.bulk_message_id,
                                'number_type': 'spam'
                            },

                        'on_success': (data) => {
                                this.update();
                            },

                        'on_error': 'Unable to add dummy recipients',
                    });


                }
            );
        },

        onAddAllListMembers(list)
        {
            this.$rc.rest({
                'spinner': true,
                'endpoint': 'PUT: /api/v1/bulk/recipients/list-to-preview',

                'params' : {
                    'list_id' : list,
                    'bulk_message_id' : this.$route.params.bulk_message_id,
                    'rest_timeout': 60000,
                },

                'on_success': (data) => {
                        this.$rc.hideSpinner();

                        this.$router.push({
                            'name': 'bulk-recipient-preview',
                            'params' : {
                                'organization_id' : data.organization_id,
                                'bulk_message_id' : data.bulk_message_id,
                                'import_id' : data.import_id
                            }
                        });
                    },

                'on_error': 'Unable to add list members to recipients',
            });
        },

        onChangeBlobs()
        {
            this.$rc.getBlobMetaDataMultiple(
                this.bulk_message.blob_ids,

                (data) => {
                    this.$rc.hideSpinner();

                    this.$refs['bulk-message-attachment-warning-dialog'].showDialog(
                        data.meta_data,

                        () => {
                            this.upsertBulkMessage();
                        },

                        () => {
                            this.bulk_message.blob_ids = [];
                            this.upsertBulkMessage();
                        },

                    );
                },

                () => {
                    this.$rc.hideSpinner();
                    this.$rc.alert('Error', 'Unable to get blob meta data');
                }
            );
        },

        onClickOrderAttribution()
        {
             this.$router.push({
                'name': 'analytics-one-bulk-message-analytics-group',
                'params' : {
                'organization_id': this.bulk_message.organization_id,
                'analytics_group_id': this.bulk_message.comp_analytics_group_id,
                }
            });
        },

        onClickCancelScheduledSend()
        {
            this.$rc.confirm({
                'title': 'Cancel scheduled bulked message',
                'body': 'Are you sure that you would like to cancel the scheduled send of this bulk message?',
                'btn_text_ok': 'Cancel the scheduled send',
                'btn_text_cancel': 'No',
                'on_ok': () => {
                    this.doCancelScheduledSend();
                },
            });
        },

        doCancelScheduledSend()
        {
            this.bulk_message.scheduled_send_time = null;
            this.upsertBulkMessage();
        },

        onScheduleTime()
        {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/bulk/schedule-warnings',

                'params' : {
                        'bulk_message_id': this.bulk_message.bulk_message_id,
                        'time': this.bulk_message.scheduled_send_time,
                    },

                'on_success': (data) => {

                        if ( data.warning == null )
                        {
                            this.onScheduleTimeFrontEndWarnings();
                            return;
                        }

                        // Unschedule the message
                        this.bulk_message.scheduled_send_time = null;
                        this.upsertBulkMessage();

                        this.$rc.alert(
                            data.warning.title,
                            data.warning.body_html,
                            () => {}
                        );
                    },

                'on_error': 'Unable to check for scheduling time warnings',
            });
        },

        onScheduleTimeFrontEndWarnings()
        {
            if ( this.bulk_message.scheduled_send_time < Date.now() )
            {
                this.$rc.snackbar("You can not schedule a send time to be in the past");
                this.bulk_message.scheduled_send_time = null;
                return;
            }

            let date = new Date(this.bulk_message.scheduled_send_time);

            if ( date.getHours() < 6 || date.getHours() > 18 )
            {
                this.$rc.confirm(
                    'Unusual scheduled time',
                    'You have scheduled this message to go out earlier than 6:00 AM or after 7:00 PM.  Are you sure?',
                    [
                        {'text': 'Cancel', 'value': 'cancel'},
                        {'text': 'Send at this time', 'value': 'confirm'},
                    ],

                    (response) => {
                        if (response != 'confirm')
                        {
                            this.bulk_message.scheduled_send_time = null;
                            return;
                        }

                        this.bulk_message.scheduled_by_employee_id = this.$rc.$state.getEmployeeId();

                        this.doScheduleMessage();
                    }
                );
            }
            else
            {
                this.bulk_message.scheduled_by_employee_id = this.$rc.$state.getEmployeeId();

                this.doScheduleMessage();

            }
        },

        doScheduleMessage()
        {
            this.$rc.rest({
                'spinner': true,
                'endpoint': 'PUT: /api/v1/bulk/messages/schedule-bulk-message',

                'params' : {
                        ... this.bulk_message,
                        'specimen_bulk_message_attestation' : this.$refs['bulk_message_attestation'].getSpecimen(),
                    },

                'on_success': (data) => {
                        if ( data.attestation_signature_error_message != null )
                        {
                            this.$rc.alert('Unable to schedule bulk message', data.attestation_signature_error_message);
                            this.update();
                            return;
                        }

                        this.$rc.alert('Warning', 'You have scheduled a bulk message to be sent at a future time.<br/><br/>Consumers expect someone to be around to answer responses to bulk messages.  Make sure you are prepared to do this.<br/><br>Sending time is approximate (may be delayed by up to 5 minutes).');
                    },

                'on_error': 'Unable to complete API call',
            });


        },

        onClickValidation()
        {
            this.$router.push({
                'name': 'search-endpoint-validation-details',
                'params' : {
                    'organization_id' : this.$route.params.organization_id
                }
            });
        },

        onClickViewImports()
        {
            this.$refs['bulk-message-search-imports-dialog'].showDialog();
        },

        onClickGoogle()
        {
            window.open('https://www.google.com','_blank');
        },

        onClickTemplates()
        {
            this.$refs['bulk-message-template-pick-dialog'].showDialog();
        },

        onClickRedChirpAIImproveContent()
        {
            this.$rc.startProcess({
                'type': 'inference-model-r1-message-content',

                'params': {
                    'bulk_message_id': this.$route.params.bulk_message_id,
                    'force_refresh_inference': false,
                },

                'title': 'RedChirp AI: Suggestions to Improve Message Content',
                'finished_button_text': 'View Suggestions',
                'failed_button_text': 'Ok',

                status: (pte) => {
                    switch (pte.state)
                    {
                        case 'failed': return 'Unable to generate improvement suggestions';
                        case 'finished': return 'Suggestions generated';
                    }

                    return 'Generating message content improvement suggestions';
                },

                on_finished: (pte) => {

                    this.$refs['m-model-r1-suggestions-to-improve-message-content-dialog'].showDialog(pte.output);
                },

                on_failed: (pte) => {
                    this.update();
                },

            });

        },

        onClickChangeVariableStyle()
        {
            if ( this.use_old_message_patterns )
            {
                this.$rc.confirm({
                    'title': 'Switch message to use new style variables',
                    'body': 'Are you certain that you would like to switch over to use new style variables? (e.g. {first-name})',
                    'btn_text_ok': 'Use new style variables',
                    'btn_text_cancel': 'Cancel',
                    'on_ok': () => {

                        this.bulk_message.message_patterns = [
                            'Hi {first-name}, this is {organization-label}.\n\nYour message goes here.\n\nText STOP to opt-out.'
                        ];

                        this.bulk_message.use_legacy_variables = false;

                        this.upsertBulkMessage();
                    },
                });
            }
            else
            {
                this.$rc.confirm({
                    'title': 'Switch message to use old style variables',
                    'body': 'Are you certain that you would like to switch over to use old style variables? (e.g. %first-name%)',
                    'btn_text_ok': 'Use legacy variables',
                    'btn_text_cancel': 'Cancel',
                    'on_ok': () => {

                        this.bulk_message.message_patterns = [
                        ];

                        this.bulk_message.use_legacy_variables = true;

                        this.upsertBulkMessage();
                    },
                });
            }
        },

        onClickUpdateRecipientLocations()
        {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/bulk/compute-missing-location',

                'params' : {
                        'bulk_message_id': this.$route.params.bulk_message_id,
                        'recompute_all': true,
                    },

                'on_success': (data) => {
                        this.$rc.alert('Updating Recipient Location', 'The process of updating the location for all recipients has been started.  This may take several minutes.<br/><br/>You can use the Refresh menu item to view the results as they are obtained.');
                    },

                'on_error': 'Unable to compute locations',
            });
        },

        onClickLocationDetails(item)
        {
            this.$rc.rest({
                'spinner': true,
                'endpoint': 'GET: /api/v1/bulk/location/:bulk_message_recipient_id',

                'params' : {
                        'bulk_message_recipient_id': item.bulk_message_recipient_id,
                    },

                'on_success': (data) => {
                        if ( data.technical_details == null )
                        {
                            this.$rc.alert('No location', 'This recipient does not have a location.  Use the "Update recipient locations..." menu item to update locations for all recipients');
                        }
                        else
                        {
                            this.$rc.technicalDetails({
                                'title': 'Location Technical Details',
                                'technical_details': data.technical_details,
                            });


                        }
                    },

                'on_error': 'Unable to complete API call',
            });
        },

        onClickUpdateNumberReassignmentCheckSinceDates()
        {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/bulk/compute-missing-number-reassignment-check-since-dates',

                'params' : {
                        'bulk_message_id': this.$route.params.bulk_message_id,
                        'recompute_all': true,
                    },

                'on_success': (data) => {
                        this.$rc.alert('Updating number reassignment check since dates', 'The process of updating the number reassignment check since dates for all recipients has been started.  This may take several minutes.<br/><br/>You can use the Refresh menu item to view the results as they are obtained.');
                    },

                'on_error': 'Unable to compute number reassignment check since dates',
            });
        },

        onClickNumberReassignmentCheckSinceDateDetails(item)
        {
            this.$rc.rest({
                'spinner': true,
                'endpoint': 'GET: /api/v1/bulk/number-reassignment-check-since-date/:bulk_message_recipient_id',

                'params' : {
                        'bulk_message_recipient_id': item.bulk_message_recipient_id,
                    },

                'on_success': (data) => {
                        if ( data.technical_details == null )
                        {
                            this.$rc.alert('No number reassignment check since date', 'This recipient does not have a number reassignment check since date.  Use the "Update number reassignment check since dates..." menu item to update all recipients');
                        }
                        else
                        {
                            this.$rc.technicalDetails({
                                'title': 'Number Reassignment Check Since Date Technical Details',
                                'technical_details': data.technical_details,
                            });


                        }
                    },

                'on_error': 'Unable to complete API call',
            });
        },

        onClickStartImportWinePulse()
        {
            this.$refs['import-wine-pulse-report-dialog'].showDialog();
        },

        onImportWinePulseReport(blobs)
        {
            if ( blobs.length == 0 )
                return;

            let blob_id = blobs[0];

            this.$rc.rest({
                'spinner': true,
                'endpoint': 'PUT: /api/v1/bulk/recipients/wine-pulse-import',

                'params' : {
                        'bulk_message_id' : this.$route.params.bulk_message_id,
                        'blob_id': blob_id,
                    },

                'on_success': (data) => {
                        this.$router.push({
                            'name': 'bulk-recipient-preview',
                            'params' : {
                                'organization_id' : data.organization_id,
                                'bulk_message_id' : data.bulk_message_id,
                                'import_id' : data.import_id
                            }
                        });
                    },

                'on_error': 'Unable to import WinePulse report',
            });
        },

        onClickAddRecipientsUsingReservations(content_type)
        {
            this.$router.push({
                'name': 'crm-reservations',
                'params' : {
                    'organization_id': this.$route.params.organization_id,
                    'content_type': content_type,
                },
                'query' : {
                    'add_to_bulk_message_id': this.$route.params.bulk_message_id,
                },
            });
        },

        onViewSendSummary()
        {
            let url = this.$router.resolve({
                'name': 'view-bulk-message-send-summary',
                'params' : {
                    'organization_id' : this.$route.params.organization_id,
                    'bulk_message_id' : this.$route.params.bulk_message_id,
                }
            });

            window.open(url.href, '_blank');
        },


        onClickRemoveRecipientsFromRecentOrders()
        {
            this.$router.push({
                'name': 'bulk-recipient-remove-recent-order',
                'params' : {
                    'organization_id' : this.bulk_message.organization_id,
                    'bulk_message_id' : this.bulk_message.bulk_message_id,
                }
            }).catch(e=>{}); // swallow any exception
        },
    },



    computed: {

        feature_available_rc_ai_improve_content()
        {
            return this.$rc.isAuthActorSystemAdmin();
        },

        show_add_recipients_from_list_menu()
        {
            if ( this.$rc.$state.isCRMEnabled('commerce7') ) return true;
            if ( this.$rc.$state.isCRMEnabled('tock-v2') ) return true;
            if ( this.$rc.$state.isCRMEnabled('ecellar') ) return true;
            if ( this.$rc.$state.isCRMEnabled('offset') ) return true;

            return false;
        },

        add_recipients_from_list_menu_items()
        {
            let ret = [];

            ret.push({text: 'Add recipients from a list...', action: this.onClickAddAllListMembers});

            if ( this.$rc.$state.isCRMEnabled('commerce7') )
            {
                ret.push({text: 'Add recipients using a Commerce7 tag...', action: this.onClickAddCommerce7Tag});

                ret.push({text: 'Add recipients that are active club members in Commerce7...', action: this.onClickAddCommerce7Club});
                ret.push({text: 'Add recipients who have not customized their club shipment...', action: this.onClickAddNotCustomClubShipment, badge_new: true});

                ret.push({text: 'Add guests from Commerce7 reservations...', action: () => this.onClickAddRecipientsUsingReservations('commerce7')});
            }

            if ( this.$rc.$state.isCRMEnabled('tock-v2') )
            {
                ret.push({text: 'Add guests from Tock reservations...', action: () => this.onClickAddRecipientsUsingReservations('tock-v2')});
            }

            if ( this.$rc.$state.isCRMEnabled('ecellar') )
            {
                ret.push({text: 'Add guests from eCellar reservations...', action: () => this.onClickAddRecipientsUsingReservations('ecellar')});
            }

            if ( this.$rc.$state.isCRMEnabled('order-port') )
            {
                ret.push({text: 'Add guests from OrderPort reservations...', action: () => this.onClickAddRecipientsUsingReservations('order-port')});
            }

            if ( this.$rc.$state.isCRMEnabled('offset') )
            {
                ret.push({text: 'Add recipients from Offset groups...', action: this.onClickAddOffsetGroup, badge_new: true});
                //Hide for now
                //ret.push({text: 'Add club members from Offset...', action: this.onClickAddOffsetClub, badge_new: true});
            }

            return ret;
        },

        message_type() {
            if ( this.message_type_force_mms )
                return 'MMS';

            if ( this.bulk_message.vcard_id != null )
                return 'MMS';

            // Use current example message from the message patterns component if available
            if (this.current_example_message && this.current_example_message !== 'Could not generate a message') {
                return this.$rc.messageType(this.current_example_message, this.message_type_force_mms || this.bulk_message.vcard_id != null);
            }
            
            if ( this.old_message_text == null )
                return 'SMS';
            
            return this.$rc.messageType(this.old_message_text);
        },

        message_type_force_mms() {
            if ( this.bulk_message == null ) return false;
            if ( this.bulk_message.blob_ids == null ) return false;

            return this.bulk_message.blob_ids.length > 0;
        },

        text_message_hint_text()
        {
            return 'This will be the text that the customer will receive when the bulk message is sent. %first_name%, %last_name%, ' +
                'and %phone_number% will be replaced with their appropriate values when the text message goes out.';
        },

        recipient_headers()
        {
            let ret = [];

            ret.push({ text: 'First', value: 'recipient_contact_first_name'});
            ret.push({ text: 'Last', value: 'recipient_contact_last_name'});
            ret.push({ text: 'Phone Number', value: 'recipient_endpoint'});

            ret.push({ text: 'Location*', value: 'locations'});

            if (this.$rc.$state.isAuthActorSystemAdmin())
            {
                ret.push({ text: 'Number Reassignment Check Since Date', value: 'number_reassignment_check_since_date'});
            }

            if ( this.recipient_extra_fields )
            {
                for ( let i=0; i < this.recipient_extra_fields.length; i++ )
                {
                    let extra_field = {
                        text: this.recipient_extra_fields[i].extra_field_key,
                        value: 'extra_field_' + this.recipient_extra_fields[i].extra_field_key,
                        'sortable': false,
                    };

                    ret.push(extra_field);
                }
            }

            ret.push({ text: 'Custom Message', value: 'custom_message'});


            ret.push({ text: 'Status', value: 'status', 'sortable': false});
            ret.push({ text: 'Sent Time', value: 'sent_time'});

            if ( this.bulk_message && this.bulk_message.use_outcome_tracking )
            {
                ret.push({ text: 'Outcome', value: 'outcome_label'});
            }

            if ( this.bulk_message && this.bulk_message.sent_time )
            {
                ret.push({ text: 'Replied', value: 'replied'});
            }

            if ( this.bulk_message && this.bulk_message.link_shortener_host != null )
            {
                ret.push({ text: 'Clicks', value: 'shortened_link_click_count'});
            }

            ret.push({ text: 'Action', value: 'action', 'sortable': false});

            return ret;
        },

        outcome_menu_items()
        {
            let ret = [];

            ret.push({
               'text' : 'Edit',
               'event' : 'edit'
            });

            ret.push({
                'text' : 'Delete',
                'event' : 'delete'
            });

            return ret;
        },

        pageframe_color() {
            if ( this.$route.params.organization_id !== this.$rc.$state.organization_id )
                return '#5D647C';
            else
                return '#F24653';
        },

        has_sent() {
            if ( this.bulk_message == null ) return false;
            //if ( this.bulk_message.scheduled_send_time != null ) return true;
            return this.bulk_message.sent_time != null;
        },

        is_scheduled_but_unsent() {
             if ( this.bulk_message == null ) return false;
             if ( this.bulk_message.sent_time != null ) return false;

             if ( this.bulk_message.scheduled_send_time != null ) return true;

             return false;
        },

        has_cancellation_reason()
        {
            if (!this.has_sent) return false;
            if (this?.bulk_message.state != 'cancelled') return false;

            return this?.bulk_message?.cancellation_reason
        },

        message_variables() {

            let ret = [
                {'text' : 'First Name', 'value' : '%first_name%' },
                {'text' : 'Last Name', 'value' : '%last_name%' },
                {'text' : '%', 'value' : '%%' },
            ];

            for ( let i = 0; i < this.recipient_extra_fields.length; i++ )
            {
                let cur = this.recipient_extra_fields[i];

                ret.push(
                    {
                        'text' : cur.extra_field_key,
                        'value' : '%'+cur.extra_field_key+'%',
                    }
                );
            }

            return ret;
        },

        show_shorten_links_using()
        {
            if ( this.bulk_message == null ) return false;

            let text = this.old_message_text;
            if ( text == null ) text = '';

            text = text.toLowerCase();

            let has_link = text.includes('http://') || text.includes('https://');

            return this.bulk_message.provision_branded_link_shortener == true && has_link;
        },

        link_shortener_host_choices()
        {
            let ret = [];

            ret.push({ 'text' : 'Do not shorten links', 'value' : null});

            let hosts = this.$rc.getDeep(this.bulk_message,'link_shortener_hosts');
            if ( hosts == null ) return ret;

            for ( let i = 0; i < hosts.length; i++ )
            {
                if ( hosts[i].is_active == false ) continue;

                ret.push({ 'text' : hosts[i].host, 'value' : hosts[i].host});
            }

            return ret;
        },

        old_message_text()
        {
            if ( this.bulk_message == null )
                return null;

            if ( this.bulk_message.message_patterns.length == 0 )
                return null;

            let ret = this.bulk_message.message_patterns[0];

            if ( ret == null )
                return null;

            ret = ret.trim();

            if ( ret.length == 0 )
                return null;

            return ret;
        },

        use_old_message_patterns()
        {
            if ( this.bulk_message == null )
                return false;

            return this.bulk_message.use_legacy_variables;
        },

        are_templates_available()
        {
            return this.$rc.$state.profile.organization_account.industry == 'wine';
        },
    },

    watch: {
        table_options(newValue, oldValue) {
            this.updateRecipients();
        }
    },
}
</script>

<style lang="scss" scoped>
.heading {
    font-weight: bold;
}
.right_icon_column
{
    min-width: 60px;
}
</style>